import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Box, Chip, Typography } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useDispatch } from 'react-redux';
import { displaySuccessMessage, displayErrorMessage } from 'redux/notificationBar/notificationBarSlice';
import { bulkUpdateEventsById } from 'api/canvms';
import { useCANvisionContext } from '../../context/useCANvisionContext';
import { useCrossSiteEvents } from '../../hooks/useCrossSiteEvents';
import type { CrossSiteEventFilters } from '../../types/canvision.types';
import EventsControlBar, { type EventsFilters, type EventStatusFilter } from './EventsControlBar';
import EventsTable from './EventsTable';
import EventDetail from '../EventDetail/EventDetail';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface EventsTabProps {
  /** Optional callback when the event list changes (e.g., after bulk update) */
  onEventsChanged?: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function EventsTab({ onEventsChanged }: EventsTabProps) {
  const reduxDispatch = useDispatch();
  const { sites, lastEvent, lastEventUpdate, filterByCameraId, setFilterByCameraId, setHighlightedCameraId } = useCANvisionContext();

  // -- Filter state ---------------------------------------------------------
  const [filters, setFilters] = useState<EventsFilters>({
    siteId: null,
    type: '',
    severity: '',
    status: 'all' as EventStatusFilter,
  });

  // Translate UI filters into the shape useCrossSiteEvents expects.
  // When filterByCameraId is set (from Attention Feed click), inject camera_id.
  const apiFilters = useMemo<CrossSiteEventFilters>(() => {
    const f: CrossSiteEventFilters = {};
    if (filters.siteId) f.site_id = filters.siteId;
    if (filters.type) f.event_type = filters.type;
    if (filters.status && filters.status !== 'all') f.status = filters.status;
    if (filterByCameraId) f.camera_id = filterByCameraId;
    // Severity is not part of the API filter shape yet, so we filter client-side
    return f;
  }, [filters.siteId, filters.type, filters.status, filterByCameraId]);

  const { events, counts, loading, error, refetch } = useCrossSiteEvents(apiFilters);

  // -- Auto-refetch on new socket event (canvms:event) ----------------------
  // When the Provider receives a new event over the socket, `lastEvent`
  // changes and we silently refresh the event list in the background.
  useEffect(() => {
    if (lastEvent) {
      refetch();
    }
  }, [lastEvent, refetch]);

  // -- Auto-refetch on event status update (canvms:event-updated) ----------
  // When another operator changes an event's status, refresh the list.
  useEffect(() => {
    if (lastEventUpdate) {
      refetch();
    }
  }, [lastEventUpdate, refetch]);

  // Client-side severity filter (the API endpoint may not support it)
  const filteredEvents = useMemo(() => {
    if (!filters.severity) return events;
    return events.filter((e) => e.severity === filters.severity);
  }, [events, filters.severity]);

  // -- Selection state ------------------------------------------------------
  const [selectedEventIds, setSelectedEventIds] = useState<Set<number>>(new Set());

  const handleSelectionChange = useCallback((ids: Set<number>) => {
    setSelectedEventIds(ids);
  }, []);

  // -- Drawer state (detail drawer) -----------------------------------------
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);

  const handleEventClick = useCallback((eventId: number) => {
    setSelectedEventId(eventId);
  }, []);

  const handleDrawerClose = useCallback(() => {
    setSelectedEventId(null);
  }, []);

  // Derive siteId for the selected event (needed by EventDetail drawer)
  const selectedEventSiteId = useMemo(() => {
    if (!selectedEventId) return null;
    const event = filteredEvents.find((e) => e.id === selectedEventId);
    return event?.site_id ?? null;
  }, [selectedEventId, filteredEvents]);

  // -- Bulk actions ---------------------------------------------------------
  const executeBulkAction = useCallback(
    async (status: 'acknowledged' | 'dismissed') => {
      if (selectedEventIds.size === 0) return;
      const count = selectedEventIds.size;
      try {
        await bulkUpdateEventsById(Array.from(selectedEventIds), status);
        setSelectedEventIds(new Set());
        refetch();
        onEventsChanged?.();
        reduxDispatch(displaySuccessMessage(`${count} event${count > 1 ? 's' : ''} ${status}`));
      } catch (err: any) {
        const message = err?.response?.data?.message || err?.message || 'Bulk update failed';
        console.error('[CANvision] Bulk event update failed:', err);
        reduxDispatch(displayErrorMessage(message));
      }
    },
    [selectedEventIds, refetch, onEventsChanged, reduxDispatch],
  );

  const handleBulkAcknowledge = useCallback(
    () => executeBulkAction('acknowledged'),
    [executeBulkAction],
  );
  const handleBulkDismiss = useCallback(
    () => executeBulkAction('dismissed'),
    [executeBulkAction],
  );

  // -- Cross-section linking: row hover -> highlight camera tile ------------
  const handleRowHover = useCallback(
    (cameraId: number | null) => {
      setHighlightedCameraId(cameraId);
    },
    [setHighlightedCameraId],
  );

  // Derive camera name for the filter chip from the loaded events
  const filterCameraName = useMemo(() => {
    if (!filterByCameraId) return null;
    const match = events.find((e) => e.camera_id === filterByCameraId);
    if (match) return match.camera_display_name || match.camera_name || `Camera ${filterByCameraId}`;
    return `Camera ${filterByCameraId}`;
  }, [filterByCameraId, events]);

  /** Clear the camera filter set from the Attention Feed */
  const handleClearCameraFilter = useCallback(() => {
    setFilterByCameraId(null);
  }, [setFilterByCameraId]);

  // -- Render ---------------------------------------------------------------
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {/* Control bar with filters + bulk actions */}
      <EventsControlBar
        sites={sites}
        filters={filters}
        onFiltersChange={setFilters}
        selectedCount={selectedEventIds.size}
        onBulkAcknowledge={handleBulkAcknowledge}
        onBulkDismiss={handleBulkDismiss}
      />

      {/* "Filtered by camera" chip -- shown when Attention Feed tile was clicked */}
      {filterByCameraId && filterCameraName && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 0.5 }}>
          <Chip
            icon={<FilterListIcon />}
            label={`Filtered by: ${filterCameraName}`}
            color="info"
            variant="outlined"
            size="small"
            onDelete={handleClearCameraFilter}
            sx={{ fontWeight: 500 }}
          />
        </Box>
      )}

      {/* Events table */}
      <EventsTable
        events={filteredEvents}
        counts={counts}
        loading={loading}
        error={error}
        selectedEventIds={selectedEventIds}
        onSelectionChange={handleSelectionChange}
        onEventClick={handleEventClick}
        onRowHover={handleRowHover}
      />

      {/* Event detail drawer */}
      <EventDetail
        eventId={selectedEventId}
        siteId={selectedEventSiteId}
        open={selectedEventId !== null}
        onClose={handleDrawerClose}
        onStatusChange={() => { refetch(); onEventsChanged?.(); }}
      />
    </Box>
  );
}

export default EventsTab;
